/**
 * Conversation module-pane persisted state — pure parsing + a panels resolver,
 * at the lib (infrastructure) layer so BOTH `features/chat` (ChatBox's
 * hydration) and `app/` (AuthenticatedAppShell's first-paint readiness gate)
 * can use it without an `app ↔ features/chat` import cycle.
 *
 * The parse primitives (`parseChatPanePanelState` / `getChatPaneStateTimestamp`)
 * were moved here from `features/chat/utils/chat-pane-state.ts`, which now
 * re-exports them so chat consumers are unchanged. `resolveLatestOpenPane`
 * factors out the "most-recently-updated open chatPane wins" selection that
 * ChatBox's `hydrateModulePaneState` performs, so the shell can resolve the
 * user's last-open module before first paint.
 */
import { listConversationPanels } from '@/lib/api/endpoints/workspaces';
import type { ConversationPanel } from '@/lib/api/endpoints/workspaces';

// Inlined here (instead of importing the features/chat helper) to keep this a
// dependency-free lib module.
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export type ChatPanePanelState = {
  open: boolean;
  fullscreen: boolean;
  anchorMessageId?: string | null;
  updatedAt?: string;
};

export function parseChatPanePanelState(value: unknown): ChatPanePanelState | null {
  if (!isRecord(value)) return null;
  if (typeof value.open !== 'boolean') return null;
  const fullscreen = typeof value.fullscreen === 'boolean' ? value.fullscreen : false;
  const anchorMessageId = typeof value.anchorMessageId === 'string'
    ? value.anchorMessageId
    : value.anchorMessageId === null
      ? null
      : undefined;
  const updatedAt = typeof value.updatedAt === 'string' ? value.updatedAt : undefined;
  return { open: value.open, fullscreen, anchorMessageId, updatedAt };
}

export function getChatPaneStateTimestamp(state: ChatPanePanelState | null, fallbackIso?: string): number {
  if (state?.updatedAt) {
    const parsed = Date.parse(state.updatedAt);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (fallbackIso) {
    const parsed = Date.parse(fallbackIso);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

export interface InitialPaneState {
  moduleId: string;
  isVisible: boolean;
}

/**
 * Pick the module pane the conversation should restore: the most-recently-updated
 * non-archived panel that carries a persisted `chatPane` state. Returns null when
 * no panel has one (a conversation that was never opened to a module). Mirrors the
 * selection in ChatBox's `hydrateModulePaneState` so the shell's first-paint seed
 * agrees with ChatBox's later restore.
 */
export function resolveLatestOpenPane(panels: ConversationPanel[]): InitialPaneState | null {
  const candidates = panels
    .filter((panel) => !panel.archivedAt)
    .map((panel) => ({
      panel,
      chatPane: parseChatPanePanelState(isRecord(panel.state) ? panel.state.chatPane : undefined),
    }))
    .filter((entry) => Boolean(entry.chatPane));

  if (candidates.length === 0) return null;

  candidates.sort((left, right) => (
    getChatPaneStateTimestamp(right.chatPane, right.panel.updatedAt)
    - getChatPaneStateTimestamp(left.chatPane, left.panel.updatedAt)
  ));

  const latest = candidates[0];
  if (!latest?.panel?.moduleId || !latest.chatPane) return null;
  return { moduleId: latest.panel.moduleId, isVisible: latest.chatPane.open };
}

/**
 * Fetch the conversation's panels and resolve its persisted last-open module
 * pane. Best-effort: returns null on any error (the caller falls back to URL /
 * storage / first-module). Used by AuthenticatedAppShell's Gate 6.
 */
export async function fetchInitialPaneState(
  token: string | null,
  workspaceId: string,
  conversationId: string,
): Promise<InitialPaneState | null> {
  if (!token || !workspaceId || !conversationId) return null;
  try {
    const panels = await listConversationPanels(token, workspaceId, conversationId, {
      includeArchived: false,
    });
    return resolveLatestOpenPane(panels);
  } catch {
    return null;
  }
}
